import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KeyValueList } from '@/components/ui/key-value-list';
import type { OpportunityDetail } from '@/hooks/api/useOpportunities';
import { formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

type PolicyPartyData = Pick<
  OpportunityDetail['policy'],
  'jurisdiction' | 'owner' | 'relatedParties'
>;

interface ClientCardProps {
  policy: PolicyPartyData;
  /** Apply gradient background and shadow like the dashboard stats card */
  elevated?: boolean;
  columns?: 1 | 2;
}

function formatRelationshipType(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function ClientCard({ policy, elevated, columns = 1 }: ClientCardProps) {
  const partyList = policy.relatedParties ?? [];
  const relatedParties = partyList.length > 0 ? partyList : policy.owner ? [policy.owner] : [];

  return (
    <Card
      className={cn('flex h-full min-h-0 flex-col overflow-hidden', elevated && 'border-0')}
      style={
        elevated
          ? {
              background: 'linear-gradient(to bottom, hsl(211 47% 32%), hsl(211 47% 20%))',
              boxShadow: 'var(--shadow-2), var(--inset-highlight)',
            }
          : undefined
      }
    >
      <CardHeader
        className={cn(
          'flex flex-row items-start justify-between gap-3 pb-3',
          elevated && 'text-slate-200'
        )}
      >
        <CardTitle className={cn(elevated && 'text-slate-100')}>Policy Parties</CardTitle>
      </CardHeader>
      <CardContent
        className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', elevated && 'text-slate-200')}
      >
        {relatedParties.length > 0 ? (
          <div className="relative min-h-0 flex-1">
            <div className="scrollbar-hide h-full space-y-3 overflow-y-auto pr-2 pb-5">
              {relatedParties.map((party, index) => (
                <div
                  key={`${party.relationshipType}-${party.id ?? index}`}
                  className={cn(index > 0 && 'border-t border-white/10 pt-3')}
                >
                  <KeyValueList
                    boldValues
                    columns={columns}
                    items={[
                      { label: 'Name', value: party.name },
                      {
                        label: 'Age',
                        value: party.age !== null ? `${party.age} years old` : undefined,
                      },
                      { label: 'Jurisdiction', value: policy.jurisdiction },
                      { label: 'Date of Birth', value: formatDate(party.dateOfBirth) },
                      {
                        label: 'Relationship',
                        value: formatRelationshipType(party.relationshipType),
                      },
                    ]}
                  />
                </div>
              ))}
            </div>
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-6',
                elevated ? 'policy-parties-fade-elevated' : 'policy-parties-fade'
              )}
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No related-party information available</p>
        )}
      </CardContent>
    </Card>
  );
}
